import type { AppointmentWithDetails } from "@/hooks/useAppointments";
import type {
  AppointmentStatus,
  AppointmentStatusFinalType,
  AppointmentStatusScope,
} from "@/hooks/useAppointmentStatuses";

export const DEFAULT_STATUS_COLORS: Record<string, string> = {
  agendado: "#64748b",
  confirmado: "#3b82f6",
  em_atendimento: "#f59e0b",
  finalizado: "#14b8a6",
  cancelado: "#dc2626",
  faltou: "#0f172a",
  no_banho: "#06b6d4",
  na_tosa: "#8b5cf6",
  secando: "#f97316",
  entregue: "#059669",
  pago: "#059669",
};

export const STATUS_FINAL_TYPES = ["normal", "finalizado", "cancelado"] as const;

export const isAppointmentStatusFinalType = (value: unknown): value is AppointmentStatusFinalType =>
  typeof value === "string" &&
  (STATUS_FINAL_TYPES as readonly string[]).includes(value);

export const getStatusColor = (statusName: string, statuses?: AppointmentStatus[]): string =>
  statuses?.find((status) => status.name === statusName)?.color ??
  DEFAULT_STATUS_COLORS[statusName] ??
  DEFAULT_STATUS_COLORS.agendado;

const clampColorChannel = (value: number) => Math.min(255, Math.max(0, Math.round(value)));

const hexToRgb = (color: string): { r: number; g: number; b: number } | null => {
  const normalized = color.trim().toLowerCase();
  const match = normalized.match(/^#([0-9a-f]{6})$/);
  if (!match) return null;

  const hex = match[1];
  return {
    r: Number.parseInt(hex.slice(0, 2), 16),
    g: Number.parseInt(hex.slice(2, 4), 16),
    b: Number.parseInt(hex.slice(4, 6), 16),
  };
};

const rgbToHex = ({ r, g, b }: { r: number; g: number; b: number }): string =>
  `#${clampColorChannel(r).toString(16).padStart(2, "0")}${clampColorChannel(g).toString(16).padStart(2, "0")}${clampColorChannel(b).toString(16).padStart(2, "0")}`;

const mixHex = (color: string, mixWith: string, weight: number): string => {
  const base = hexToRgb(color);
  const overlay = hexToRgb(mixWith);
  if (!base || !overlay) return color;

  return rgbToHex({
    r: base.r * (1 - weight) + overlay.r * weight,
    g: base.g * (1 - weight) + overlay.g * weight,
    b: base.b * (1 - weight) + overlay.b * weight,
  });
};

const getReadableTextColor = (color: string): string => {
  const rgb = hexToRgb(color);
  if (!rgb) return "#ffffff";

  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.62 ? "#111827" : "#ffffff";
};

export const getStatusBadgeStyle = (statusName: string, statuses?: AppointmentStatus[]) => {
  const color = getStatusColor(statusName, statuses);
  return {
    backgroundColor: color,
    borderColor: color,
    color: getReadableTextColor(color),
  };
};

export const getStatusSoftStyle = (statusName: string, statuses?: AppointmentStatus[]) => {
  const color = getStatusColor(statusName, statuses);
  return {
    color,
    backgroundColor: mixHex(color, "#ffffff", 0.88),
  };
};

export const getAppointmentStatusScope = (behavior?: string | null): AppointmentStatusScope =>
  behavior === "banho_tosa" ? "grooming" : "appointment";

export const getEffectiveAppointmentStatusName = ({
  status,
  grooming_status,
  clinic_appointment_type,
}: Pick<AppointmentWithDetails, "status" | "grooming_status" | "clinic_appointment_type">): string =>
  clinic_appointment_type?.behavior === "banho_tosa"
    ? (grooming_status ?? status ?? "agendado")
    : (status ?? "agendado");

export const getStatusConfigByName = (
  statusName: string,
  statuses?: AppointmentStatus[],
): AppointmentStatus | undefined => statuses?.find((status) => status.name === statusName);

export const getFirstActiveStatus = (
  statuses?: AppointmentStatus[],
  fallback = "agendado",
): AppointmentStatus | undefined =>
  [...(statuses ?? [])]
    .filter((status) => status.is_active)
    .sort((left, right) => left.sort_order - right.sort_order || left.name.localeCompare(right.name))[0] ??
  (fallback ? statuses?.find((status) => status.name === fallback) : undefined);

export const getFirstActiveStatusName = (
  statuses?: AppointmentStatus[],
  fallback = "agendado",
): string => getFirstActiveStatus(statuses, fallback)?.name ?? fallback;

export const isClosedStatus = (
  statusName: string | null | undefined,
  statuses?: AppointmentStatus[],
): boolean => {
  if (!statusName) return false;
  const finalType = getStatusConfigByName(statusName, statuses)?.final_type;
  return finalType === "finalizado" || finalType === "cancelado";
};

